import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import ChatEmbedWidget from './ChatEmbedWidget';
import VoiceCallWidget from './VoiceCallWidget';
import {
  MessageSquare, MessageCircle, Phone, Minimize2, X, Loader2,
  MessagesSquare, Mic, Headphones, Bot, User,
} from 'lucide-react';

// ─── Launcher icon map ────────────────────────────────────────────────────────

export const COMBO_LAUNCHER_ICONS = {
  'messages-square': MessagesSquare,
  'message-square': MessageSquare,
  'message-circle': MessageCircle,
  phone: Phone,
  mic: Mic,
  headphones: Headphones,
  bot: Bot,
  user: User,
};

/** Resolve which icon to show on the floating launcher button */
export const resolveComboLauncherIconId = (settings, agent, channel) => {
  const iconSetting = settings?.launcherIcon || 'dynamic';
  if (iconSetting === 'dynamic') {
    if (channel === 'voice') {
      return agent?.voice_widget_icon || 'phone';
    }
    return agent?.chat_button_icon || 'message-square';
  }
  return iconSetting in COMBO_LAUNCHER_ICONS ? iconSetting : 'messages-square';
};

export const ComboLauncherIcon = ({ iconId, className = 'w-6 h-6' }) => {
  const Icon = COMBO_LAUNCHER_ICONS[iconId] || MessagesSquare;
  return <Icon className={className} />;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const resolveTheme = (mode) => {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '99, 102, 241';
};

const positionStyles = (position) => {
  const map = {
    'bottom-right': { bottom: '1.5rem', right: '1.5rem' },
    'bottom-left': { bottom: '1.5rem', left: '1.5rem' },
    'top-right': { top: '1.5rem', right: '1.5rem' },
    'top-left': { top: '1.5rem', left: '1.5rem' },
  };
  return map[position] || map['bottom-right'];
};

/** Hide panel visually while keeping children mounted (active voice calls) */
const PANEL_KEEP_ALIVE_STYLE = {
  position: 'fixed',
  left: 0,
  top: 0,
  width: 2,
  height: 2,
  opacity: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: -1,
};

const getSettingsFromAgent = (agent, urlParams) => {
  const p = urlParams || new URLSearchParams();
  return {
    themeMode: p.get('theme') || agent?.combo_widget_theme_mode || agent?.chat_theme_mode || 'light',
    primaryColor: p.get('primary')
      ? `#${p.get('primary').replace('#', '')}`
      : agent?.combo_widget_primary_color || agent?.chat_primary_color || '#6366f1',
    buttonColor: p.get('btnColor')
      ? `#${p.get('btnColor').replace('#', '')}`
      : agent?.combo_widget_button_color || agent?.chat_button_color || '#6366f1',
    position: p.get('position') || agent?.combo_widget_button_position || agent?.chat_button_position || 'bottom-right',
    buttonLabel: agent?.combo_widget_button_label || 'Contact us',
    title: agent?.combo_widget_title || agent?.name || 'Assistant',
    subtitle: agent?.combo_widget_subtitle || 'Chat or call — your choice',
    defaultChannel: p.get('channel') || agent?.combo_widget_default_channel || 'chat',
    pulseAnimation: agent?.combo_widget_pulse_animation ?? true,
    showLabel: agent?.combo_widget_show_label ?? true,
    showChannelSwitch: agent?.combo_widget_show_channel_switch ?? true,
    launcherIcon: p.get('launcherIcon') || agent?.combo_widget_launcher_icon || 'dynamic',
  };
};

// ─── Channel flip toggle ──────────────────────────────────────────────────────

const ChannelToggle = ({ channel, onChange, primaryColor, isDark, disabled, compact }) => (
  <div
    className="relative flex rounded-full p-0.5"
    style={{
      backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.25)',
      minWidth: compact ? 120 : 148,
    }}
    role="tablist"
    aria-label="Switch between chat and voice"
  >
    <div
      className="absolute top-0.5 bottom-0.5 rounded-full transition-all duration-300 ease-out"
      style={{
        width: 'calc(50% - 2px)',
        left: channel === 'chat' ? '2px' : 'calc(50%)',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    />
    {[
      { id: 'chat', label: 'Chat', Icon: MessageSquare },
      { id: 'voice', label: 'Voice', Icon: Phone },
    ].map(({ id, label, Icon }) => (
      <button
        key={id}
        type="button"
        role="tab"
        aria-selected={channel === id}
        disabled={disabled}
        onClick={() => onChange(id)}
        className="relative z-10 flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-full text-xs font-semibold transition-colors duration-200 disabled:opacity-50"
        style={{
          color: channel === id ? primaryColor : 'rgba(255,255,255,0.85)',
        }}
      >
        <Icon className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {!compact && label}
      </button>
    ))}
  </div>
);

// ─── Main unified widget shell ────────────────────────────────────────────────

const UnifiedWidgetShell = ({
  agentId,
  agent,
  settings,
  channel,
  onChannelChange,
  isMinimized,
  onMinimizeToggle,
  embedToken,
  voiceRef,
  onVoiceStateChange,
  showHeader = true,
  fullHeight = true,
}) => {
  const isDark = resolveTheme(settings.themeMode) === 'dark';
  const [isFlipping, setIsFlipping] = useState(false);
  const chatWidgetKeyRef = useRef('combo-chat-widget');

  const handleChannelSwitch = useCallback(async (newChannel) => {
    if (newChannel === channel) return;
    if (channel === 'voice' && voiceRef.current?.isConnected) {
      await voiceRef.current.endCall();
    }
    setIsFlipping(true);
    setTimeout(() => {
      onChannelChange(newChannel);
      setIsFlipping(false);
    }, 180);
  }, [channel, onChannelChange, voiceRef]);

  const bgColor = isDark ? '#1f2937' : '#ffffff';
  const borderColor = isDark ? '#374151' : '#e5e7eb';

  const showVoiceTranscript = agent?.voice_widget_show_transcript ?? true;
  // Combo accent color for waveform/spinner — must contrast on light panel backgrounds
  const voiceTheme = {
    mode: settings.themeMode,
    primaryColor: settings.primaryColor,
    buttonColor: settings.buttonColor,
    avatarColor: agent?.voice_widget_avatar_color || settings.primaryColor,
    showTranscript: showVoiceTranscript,
  };

  return (
    <div
      className={`flex flex-col overflow-hidden ${fullHeight && !isMinimized ? 'h-full' : ''}`}
      style={{ backgroundColor: bgColor, border: showHeader ? undefined : `1px solid ${borderColor}` }}
    >
      {showHeader && (
        <div
          className={`text-white flex items-center justify-between flex-shrink-0 rounded-t-2xl ${
            isMinimized ? 'px-3 py-2' : 'p-4'
          }`}
          style={{ background: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.buttonColor})` }}
        >
          <div className={`flex items-center min-w-0 flex-1 ${isMinimized ? 'gap-2' : 'gap-3'}`}>
            {!isMinimized && (
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <MessagesSquare className="w-4 h-4" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-sm font-semibold truncate">{settings.title}</h3>
              {!isMinimized && (
                <p className="text-xs text-white/70 truncate">{settings.subtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            {settings.showChannelSwitch && (
              <ChannelToggle
                channel={channel}
                onChange={handleChannelSwitch}
                primaryColor={settings.primaryColor}
                isDark={isDark}
                compact={isMinimized}
              />
            )}
            {onMinimizeToggle && (
              <button
                type="button"
                onClick={onMinimizeToggle}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                aria-label={isMinimized ? 'Maximize' : 'Minimize'}
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Keep chat/voice mounted while minimized so active calls stay connected */}
      <div
        className={isMinimized ? undefined : 'flex-1 min-h-0 relative overflow-hidden'}
        style={
          isMinimized
            ? PANEL_KEEP_ALIVE_STYLE
            : undefined
        }
        aria-hidden={isMinimized}
      >
          <style>{`
            @keyframes comboFlipIn {
              0% { opacity: 0; transform: perspective(800px) rotateY(-12deg) scale(0.97); }
              100% { opacity: 1; transform: perspective(800px) rotateY(0deg) scale(1); }
            }
            .combo-panel-active { animation: comboFlipIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
          `}</style>
          <div
            className={`absolute inset-0 ${channel === 'chat' && !isFlipping ? 'combo-panel-active' : ''}`}
            style={{ display: channel === 'chat' ? 'block' : 'none' }}
          >
            <ChatEmbedWidget
              key={chatWidgetKeyRef.current}
              agentId={agentId}
              embedToken={embedToken}
              onClose={() => {}}
              themeOverride={{
                mode: settings.themeMode,
                primaryColor: settings.primaryColor,
              }}
            />
          </div>
          <div
            className={`absolute inset-0 flex flex-col justify-center ${channel === 'voice' && !isFlipping ? 'combo-panel-active' : ''}`}
            style={{ display: channel === 'voice' ? 'flex' : 'none' }}
          >
            <VoiceCallWidget
              ref={voiceRef}
              agentId={agentId}
              viewMode="panel"
              hideHeader
              themeOverride={voiceTheme}
              onCallStateChange={onVoiceStateChange}
            />
          </div>
        </div>
    </div>
  );
};

const COMBO_MINIMIZED_H = 56;
const COMBO_CHAT_H = 600;

/** Compute floating panel height from channel + voice transcript setting */
const getComboPanelHeight = (channel, agent, isMinimized, voiceConnected = false, voiceConnecting = false) => {
  if (isMinimized) return COMBO_MINIMIZED_H;
  if (channel === 'chat') return COMBO_CHAT_H;
  const showTranscript = agent?.voice_widget_show_transcript ?? true;
  const hasWelcome = Boolean(agent?.voice_widget_welcome_message?.trim());
  if (showTranscript && voiceConnected) return COMBO_CHAT_H;
  if (!showTranscript) {
    if (voiceConnected || voiceConnecting) return 220;
    return hasWelcome ? 220 : 168;
  }
  if (voiceConnecting) return 200;
  // Transcript enabled but idle — compact until call starts
  return hasWelcome ? 220 : 168;
};

// ─── Floating launcher + expanded panel ───────────────────────────────────────

const UnifiedEmbedWidget = ({ agentId, viewMode: initialViewMode }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [channel, setChannel] = useState('chat');
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [settings, setSettings] = useState(null);
  const voiceRef = useRef(null);
  const embedTokenRef = useRef(null);

  const [viewMode] = useState(() => {
    if (initialViewMode) return initialViewMode;
    if (typeof window === 'undefined') return 'button';
    const m = new URLSearchParams(window.location.search).get('view');
    if (m === 'full') return 'full';
    if (m === 'inline' || m === 'minimal') return 'inline';
    return 'button';
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || params.get('embed_token') || params.get('key');
    if (t) embedTokenRef.current = t;
  }, []);

  useEffect(() => {
    if (!agentId) { setLoading(false); return; }
    api.get(`/agents/${agentId}`)
      .then(r => {
        setAgent(r.data);
        const s = getSettingsFromAgent(r.data);
        setSettings(s);
        setChannel(s.defaultChannel === 'voice' ? 'voice' : 'chat');
      })
      .catch(() => setError('Agent not found'))
      .finally(() => setLoading(false));
  }, [agentId]);

  useEffect(() => {
    if (viewMode !== 'button') return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = [html.style.background, body.style.background, root?.style?.background];
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    if (root) root.style.background = 'transparent';
    return () => {
      html.style.background = prev[0];
      body.style.background = prev[1];
      if (root) root.style.background = prev[2] || '';
    };
  }, [viewMode]);

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500 text-sm">{error}</div>
    );
  }

  const isDark = resolveTheme(settings.themeMode) === 'dark';
  const pos = positionStyles(settings.position);
  const btnRgb = hexToRgb(settings.buttonColor);

  const shellProps = {
    agentId,
    agent,
    settings,
    channel,
    onChannelChange: setChannel,
    embedToken: embedTokenRef.current,
    voiceRef,
    onVoiceStateChange: ({ isConnected, isConnecting }) => {
      setVoiceConnected(isConnected);
      setVoiceConnecting(isConnecting);
    },
  };

  // Full-page embed
  if (viewMode === 'full') {
    const fullIsDark = resolveTheme(settings.themeMode) === 'dark';
    const fullHeight = getComboPanelHeight(channel, agent, false, voiceConnected, voiceConnecting);
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: fullIsDark
            ? 'linear-gradient(135deg, #0f172a, #1e293b)'
            : 'linear-gradient(135deg, #eef2ff, #f8fafc)',
        }}
      >
        <div
          className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl transition-all duration-300"
          style={{
            height: fullHeight,
            border: `1px solid ${fullIsDark ? '#374151' : '#e5e7eb'}`,
          }}
        >
          <UnifiedWidgetShell {...shellProps} showHeader fullHeight />
        </div>
      </div>
    );
  }

  // Inline / minimal embed
  if (viewMode === 'inline') {
    const inlineHeight = getComboPanelHeight(channel, agent, false, voiceConnected, voiceConnecting);
    return (
      <div
        className="w-full rounded-2xl overflow-hidden transition-all duration-300"
        style={{ height: inlineHeight }}
      >
        <UnifiedWidgetShell {...shellProps} showHeader fullHeight />
      </div>
    );
  }

  // Floating button mode
  const panelHeight = getComboPanelHeight(channel, agent, isMinimized, voiceConnected, voiceConnecting);
  const panelWidth = isMinimized ? 320 : 384;
  const launcherIconId = resolveComboLauncherIconId(settings, agent, channel);

  const sessionActive = voiceConnected || voiceConnecting;

  return (
    <>
      <style>{`
        @keyframes comboPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(${btnRgb}, 0.45); }
          50% { box-shadow: 0 0 0 14px rgba(${btnRgb}, 0); }
        }
        @keyframes comboSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes comboSlideUpCentered {
          from { opacity: 0; transform: translate(-50%, calc(-50% + 16px)) scale(0.96); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes comboPop {
          0% { transform: scale(0.85); opacity: 0; }
          60% { transform: scale(1.08); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes comboIconSwap {
          0% { opacity: 0; transform: scale(0.6) rotate(-20deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        /* On phones, center the open panel with equal gaps on every side
           instead of anchoring it near the launcher corner (which left
           uneven left/right vs top/bottom margins). The minimized strip
           keeps its corner anchor since it's meant to sit next to the
           launcher button. Gated on pointer:coarse (touch input) so a
           narrow-but-mouse-driven laptop/desktop window never gets mistaken
           for a phone here - width alone isn't reliable once OS display
           scaling is involved. */
        @media (max-width: 640px) and (pointer: coarse) {
          .combo-embed-window {
            top: 50% !important;
            left: 50% !important;
            right: auto !important;
            bottom: auto !important;
            width: min(320px, calc(100vw - 3rem)) !important;
            transform: translate(-50%, -50%) !important;
            transform-origin: center !important;
            animation: comboSlideUpCentered 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
          }
        }
      `}</style>

      {(isOpen || sessionActive) && (
        <div
          className={
            isOpen
              ? `fixed z-[9999998] rounded-2xl overflow-hidden shadow-2xl transition-all duration-500 ease-out ${!isMinimized ? 'combo-embed-window' : ''}`
              : undefined
          }
          style={
            isOpen
              ? {
                  width: `min(${panelWidth}px, calc(100vw - 2rem))`,
                  height: `min(${panelHeight}px, 520px, calc(100vh - 6rem))`,
                  ...pos,
                  ...(settings.position.startsWith('bottom')
                    ? { bottom: `calc(${pos.bottom || '1.5rem'} + 4.5rem)` }
                    : { top: `calc(${pos.top || '1.5rem'} + 4.5rem)` }),
                  animation: isMinimized ? undefined : 'comboSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  transition: 'width 0.3s ease',
                }
              : PANEL_KEEP_ALIVE_STYLE
          }
          aria-hidden={!isOpen}
        >
          <UnifiedWidgetShell
            {...shellProps}
            showHeader
            fullHeight
            isMinimized={isMinimized}
            onMinimizeToggle={() => setIsMinimized(m => !m)}
          />
        </div>
      )}

      <div className="fixed z-[9999999] flex flex-col gap-2 items-end" style={pos}>
        {settings.showLabel && !isOpen && (
          <div
            className="rounded-full px-3.5 py-1.5 text-sm font-semibold shadow-md whitespace-nowrap"
            style={{
              backgroundColor: isDark ? '#1f2937' : '#ffffff',
              color: isDark ? '#f9fafb' : '#111827',
              border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            }}
          >
            {settings.buttonLabel}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            if (isOpen) handleClose();
            else { setIsOpen(true); setIsMinimized(false); }
          }}
          aria-label={isOpen ? 'Hide widget' : sessionActive ? 'Return to call' : 'Open chat and voice widget'}
          className="w-[60px] h-[60px] rounded-full flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95"
          style={{
            backgroundColor: settings.buttonColor,
            animation: settings.pulseAnimation && !isOpen ? 'comboPulse 2s ease-in-out infinite' : 'comboPop 0.4s ease-out',
            boxShadow: `0 4px 20px rgba(${btnRgb}, 0.4)`,
          }}
        >
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <span
              key={launcherIconId}
              className="flex items-center justify-center"
              style={{ animation: 'comboIconSwap 0.25s cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <ComboLauncherIcon iconId={launcherIconId} className="w-6 h-6" />
            </span>
          )}
        </button>
        {(voiceConnected || voiceConnecting) && !isOpen && (
          <span
            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-green-400 border-2 animate-pulse"
            style={{ borderColor: isDark ? '#1f2937' : '#ffffff' }}
            title={voiceConnecting ? 'Connecting…' : 'Call in progress'}
          />
        )}
      </div>
    </>
  );
};

export const UnifiedEmbedPage = ({ agentId }) => (
  <UnifiedEmbedWidget agentId={agentId} />
);

export default UnifiedEmbedWidget;
